import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../../../../components/atoms/button/Button'
import Text from '../../../../components/atoms/text/Text'
import LoadingData from '../../../../components/molecules/loadingData/LoadingData'
import Screen from '../../../../components/molecules/screen/Screen'
import { useProfile } from '../../../../modules/auth/hooks'
import { useCancelReservationMutation, useReservatedSeatById } from '../../../../modules/seats/hooks'
import { VARIANTS } from '../../../../types/Components.types'
import { ReactComponent as Illustration } from '../cancel/assets/warning_illustration.svg'
import styles from './CancelReservationScreen.module.css'

const CancelReservationScreen = () => {
  const navigate = useNavigate()
  const [isWorking, setIsWorking] = useState(false)
  const { bookId } = useParams() as { bookId: string }
  const { data: profile, isLoading: isLoadingProfile } = useProfile()
  const { data: reservation, isLoading: isLoadingReservation } = useReservatedSeatById({
    userId: profile?.id,
    seatId: bookId,
  })

  const isLoading = isLoadingProfile || isLoadingReservation

  const cancelReservation = useCancelReservationMutation()

  const handleConfirmClick = useCallback(async () => {
    setIsWorking(true)
    cancelReservation.mutate(
      {
        userId: profile!.id,
        seatId: reservation!.seat.id,
      },
      {
        onSuccess: () => {
          navigate('/app/book/list')
        },
        onSettled: () => {
          setIsWorking(false)
        },
      },
    )
  }, [cancelReservation, navigate, profile, reservation])

  const handleCancelClick = useCallback(() => {
    navigate(-1)
  }, [navigate])

  useEffect(() => {
    if (!isLoading && !reservation) {
      navigate(-1)
      return
    }
  }, [navigate, isLoading, reservation])

  if (!reservation || isLoading) {
    return <LoadingData />
  }

  const reservationDate = DateTime.fromISO(reservation.seat.reserved_from)

  return (
    <Screen hasDecorator decoratorSize="large">
      <div className={styles.container}>
        <div className={styles.content}>
          <Illustration />
          <Text size="l" variant={VARIANTS.primary} weight="700" className={styles.title}>
            Un momento
          </Text>
          <Text align="center">
            Si cancelas esta reserva perderás tu puesto el día{' '}
            <Text element="span" weight="600">
              {reservationDate.toLocaleString(DateTime.DATE_SHORT)}
            </Text>
            . Los días del bono serán devueltos a tu cuenta. ¿Estás seguro de que quieres cancelar?
          </Text>
        </div>

        <div className={styles.actions}>
          <Button
            inverted
            variant={VARIANTS.max}
            className={styles.action}
            onClick={handleCancelClick}
            disabled={isWorking}
          >
            No, volver atrás
          </Button>

          <Button
            variant={VARIANTS.negative}
            className={styles.action}
            onClick={handleConfirmClick}
            disabled={isWorking}
            isLoading={isWorking}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </Screen>
  )
}

export default React.memo(CancelReservationScreen)
